import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, MapPin, Sparkles } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import type { Lang } from '../i18n/translations';
type LocalizedString = {
  en: string;
  es: string;
};
type Product = {
  title: LocalizedString;
  editionLabel: LocalizedString;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
};
type Toast = {
  id: number;
  name: string;
  city: string;
  product: Product;
};
// Curated catalog — each product has bilingual title + edition label.
const CATALOG: Product[] = [{
  title: {
    en: 'Decorated Purse',
    es: 'Bolsa Decorada'
  },
  editionLabel: {
    en: 'Castle Princess Purse',
    es: 'Bolsa Princesa Castillo'
  },
  priceNumber: 110,
  priceText: '$110',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/jDL7hMPVaDESJVXhBhY163/Edit41.png'
}, {
  title: {
    en: 'Decorated Purse',
    es: 'Bolsa Decorada'
  },
  editionLabel: {
    en: 'Princess Unicorn Purse',
    es: 'Bolsa Princesa Unicornio'
  },
  priceNumber: 95,
  priceText: '$95',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/inKRHT76haaLWjg3TQPaKQ/Edit14.png'
}, {
  title: {
    en: 'Decorated Mirror & Bag Set',
    es: 'Set de Espejo y Bolsa Decorados'
  },
  editionLabel: {
    en: 'Signature Mirror Set',
    es: 'Set de Espejo Signature'
  },
  priceNumber: 165,
  priceText: '$165',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/jHTgX6d1XTcGoayVj4gxup/Edit4.jpg'
}, {
  title: {
    en: 'Custom Gift Set',
    es: 'Set de Regalo Personalizado'
  },
  editionLabel: {
    en: "Girls' Signature Gift Box",
    es: 'Caja de Regalo Signature para Niñas'
  },
  priceNumber: 175,
  priceText: '$175',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/fBDMTu4aZaTvo8spTeJkRp/Edit45.png'
}, {
  title: {
    en: 'Custom Gift Set',
    es: 'Set de Regalo Personalizado'
  },
  editionLabel: {
    en: 'Princess Gift Set',
    es: 'Set de Regalo Princesa'
  },
  priceNumber: 145,
  priceText: '$145',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/mdipD758k1F2nubGKkgNkT/Edit8.jpg'
}, {
  title: {
    en: 'Handcrafted Bow',
    es: 'Moño Hecho a Mano'
  },
  editionLabel: {
    en: 'K-Pop Saja Boys Bow',
    es: 'Moño K-Pop Saja Boys'
  },
  priceNumber: 25,
  priceText: '$25',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/oi3XJ6gWXoR6VyxuWA161j/Edit22.png'
}, {
  title: {
    en: 'Handcrafted Bow',
    es: 'Moño Hecho a Mano'
  },
  editionLabel: {
    en: 'Rainbow Sparkle Bow',
    es: 'Moño Arcoíris Brillante'
  },
  priceNumber: 22,
  priceText: '$22',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/i6QDMhSGJtopmpX4yy92jo/Edit24.png'
}, {
  title: {
    en: 'Handcrafted Bow Set',
    es: 'Set de Moños Hecho a Mano'
  },
  editionLabel: {
    en: 'Set of 4 Mini Bows',
    es: 'Set de 4 Mini Moños'
  },
  priceNumber: 40,
  priceText: '$40',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/1hvzvQXULTe17x9eRfaUWR/Edit27.png'
}, {
  title: {
    en: 'Custom Tumbler',
    es: 'Termo Personalizado'
  },
  editionLabel: {
    en: 'Lilo & Stitch Tumbler',
    es: 'Termo Lilo y Stitch'
  },
  priceNumber: 35,
  priceText: '$35',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/vjndBR1AuZ9QbJaPm8XGr1/Edit34.png'
}, {
  title: {
    en: 'Custom Tumbler',
    es: 'Termo Personalizado'
  },
  editionLabel: {
    en: 'Dragon Ball Tumbler',
    es: 'Termo Dragon Ball'
  },
  priceNumber: 32,
  priceText: '$32',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/2aAetUf4aKSfVnZUnfUz3N/Edit32.png'
}, {
  title: {
    en: 'Decorated Purse',
    es: 'Bolsa Decorada'
  },
  editionLabel: {
    en: 'Glitter Pearl Purse',
    es: 'Bolsa Brillo y Perlas'
  },
  priceNumber: 89,
  priceText: '$89',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/jAHQsjVyB8V1Cvmt5Udk1Q/Edit5.jpg'
}];
const NAMES = ['Maria G.', 'Jessica L.', 'Ashley M.', 'Sofia R.', 'Emily K.', 'Isabella P.', 'Olivia T.', 'Camila V.', 'Valentina C.', 'Sarah J.', 'Jennifer D.', 'Brittany N.', 'Stephanie H.', 'Andrea F.', 'Daniela S.', 'Gabriela O.', 'Karla M.', 'Vanessa A.', 'Natalia B.', 'Madison W.', 'Hannah E.', 'Megan T.', 'Lauren Y.', 'Amanda Q.', 'Nicole U.', 'Rachel I.', 'Samantha Z.', 'Taylor X.', 'Jasmine R.', 'Alejandra G.', 'Adriana L.', 'Patricia C.', 'Crystal H.', 'Brianna K.', 'Mia D.', 'Emma F.', 'Ava S.', 'Charlotte W.', 'Claudia P.', 'Yolanda V.'];
const CITIES = ['Puyallup, WA', 'Seattle, WA', 'Tacoma, WA', 'Federal Way, WA', 'Auburn, WA', 'Renton, WA', 'Kent, WA', 'Spokane, WA', 'Vancouver, WA', 'Bellevue, WA', 'Olympia, WA', 'Everett, WA', 'Portland, OR', 'Los Angeles, CA', 'San Diego, CA', 'Sacramento, CA', 'San Jose, CA', 'Phoenix, AZ', 'Las Vegas, NV', 'Salt Lake City, UT', 'Houston, TX', 'Dallas, TX', 'San Antonio, TX', 'Austin, TX', 'Miami, FL', 'Orlando, FL', 'Tampa, FL', 'Atlanta, GA', 'Chicago, IL', 'Denver, CO', 'New York, NY', 'Boston, MA', 'Philadelphia, PA'];
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickWeightedProduct(): Product {
  const physical = CATALOG.filter(p => p.priceNumber >= 50);
  const small = CATALOG.filter(p => p.priceNumber < 50);
  if (Math.random() < 0.7 && physical.length > 0) return pickRandom(physical);
  if (small.length > 0) return pickRandom(small);
  return pickRandom(CATALOG);
}
export function PurchaseNotifications() {
  const {
    t,
    lang
  } = useTranslation();
  const [toast, setToast] = useState<Toast | null>(null);
  const recentNamesRef = useRef<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = useRef<number>(0);
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  useEffect(() => {
    const pickName = (): string => {
      const recent = recentNamesRef.current;
      const available = NAMES.filter(n => !recent.includes(n));
      const pool = available.length > 0 ? available : NAMES;
      const name = pickRandom(pool);
      recentNamesRef.current = [...recent, name].slice(-10);
      return name;
    };
    const showOne = () => {
      idRef.current += 1;
      const next: Toast = {
        id: idRef.current,
        name: pickName(),
        city: pickRandom(CITIES),
        product: pickWeightedProduct()
      };
      setToast(next);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        setToast(null);
      }, 4000);
      const nextDelay = 4000 + 4000 + Math.random() * 7000;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(showOne, nextDelay);
    };
    timerRef.current = setTimeout(showOne, 3000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);
  // Resolve product strings against the current language at render-time,
  // so the visible toast switches instantly when language changes.
  const currentLang: Lang = lang;
  const editionLabel = toast ? toast.product.editionLabel[currentLang] : '';
  return <div className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-[90] w-[calc(100%-1.5rem)] max-w-sm pointer-events-none" aria-live="polite" aria-atomic="true" data-id="element-477">
      <AnimatePresence mode="wait" data-id="element-478">
        {toast && <motion.div key={toast.id} initial={prefersReducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={prefersReducedMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={prefersReducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30,
        scale: 0.97
      }} transition={{
        duration: prefersReducedMotion ? 0.15 : 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="pointer-events-auto bg-white rounded-2xl shadow-xl border border-lavender-100 p-3 flex items-center gap-3 backdrop-blur-sm" role="status" data-id="element-479">
            <div className="relative flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden border-2 border-white shadow-sm bg-cream-50" data-id="element-480">
              <img src={toast.product.thumbnailUrl} alt={editionLabel} className="w-full h-full object-cover" loading="lazy" data-id="element-481" />
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-lavender-500 flex items-center justify-center shadow-sm ring-2 ring-white" data-id="element-482">
                <CheckCircle2 className="w-3 h-3 text-white" data-id="element-483" />
              </div>
            </div>

            <div className="flex-1 min-w-0" data-id="element-484">
              <p className="text-[13px] sm:text-sm text-gray-800 leading-snug" data-id="element-485">
                <span className="font-semibold" data-id="element-486">{toast.name}</span>{' '}
                <span className="text-gray-500 inline-flex items-center gap-0.5" data-id="element-487">
                  <MapPin className="w-3 h-3" data-id="element-488" />
                  {toast.city}
                </span>
              </p>
              <p className="text-[13px] sm:text-sm text-gray-700 leading-snug mt-0.5 truncate" data-id="element-489">
                {t.notif.justPurchased}{' '}
                <span className="font-medium text-gray-900" data-id="element-490">
                  {editionLabel}
                </span>
              </p>
              <p className="text-[11px] sm:text-xs text-lavender-600 font-semibold mt-0.5 flex items-center gap-1" data-id="element-491">
                <Sparkles className="w-3 h-3" data-id="element-492" />
                {toast.product.priceText}
                <span className="text-gray-400 font-normal ml-auto" data-id="element-493">
                  {t.notif.justNow}
                </span>
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}